import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createTaxesT } from '../../../utils/taxesTranslations'
import { TaxContent } from '../TaxContent'
import { type TaxAnalysis } from './taxService'
import { OverviewTab } from './tabs/OverviewTab'
import { TrendsTab } from './tabs/TrendsTab'

export interface TaxSectionProps {
  entity: {
    id: string
    geo_center?: {
      lat: number
      lon: number
    }
    npa?: string
    municipality_name?: string
    canton_name?: string
  }
  userId: string
  onDataFetched: (data: TaxAnalysis) => void
  getTaxData: () => TaxAnalysis | null
  updateTaxData: (entity: any, userId: string) => Promise<TaxAnalysis | null>
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

export const TaxSection: React.FC<TaxSectionProps> = ({
  entity,
  userId,
  onDataFetched,
  getTaxData,
  updateTaxData,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createTaxesT(rawT, i18n)

  // State
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track fetched params to prevent duplicate requests
  const paramsHashRef = useRef<string>('')

  const fetchTaxData = useCallback(async () => {
    if (!entity?.id || !entity?.geo_center || !userId) {
      return
    }

    const paramsHash = JSON.stringify({
      entityId: entity.id,
      lat: entity.geo_center.lat,
      lon: entity.geo_center.lon,
    })

    // Skip if we already fetched with these exact params
    if (paramsHashRef.current === paramsHash) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const analysis = await updateTaxData(entity, userId)

      if (analysis) {
        onDataFetched(analysis)
      }
      paramsHashRef.current = paramsHash

      // Set default selected year from data
      if (analysis?.current_data?.year && !selectedYear) {
        setSelectedYear(analysis.current_data.year)
      }
    } catch (err) {
      console.error('Error fetching tax data:', err)
      setError(err instanceof Error ? err.message : t('states.fetchError'))
    } finally {
      setLoading(false)
    }
  }, [entity, userId, updateTaxData, onDataFetched, selectedYear, t])

  // Fetch when component mounts or entity changes
  useEffect(() => {
    fetchTaxData()
  }, [fetchTaxData])

  const data = getTaxData()

  // Get available years from time series
  const availableYears = React.useMemo(() => {
    if (!data?.time_series) return []
    return data.time_series.map((item) => item.year).sort((a, b) => b - a) // Most recent first
  }, [data?.time_series])

  // Get data for selected year
  const getDataForYear = useCallback(
    (year: number | null) => {
      if (!data || !year) return data

      // If it's the current year, return current data
      if (year === data.current_data.year) {
        return data
      }

      // Find year data in time series
      const yearData = data.time_series?.find((item) => item.year === year)
      if (!yearData) return data

      // Create a modified analysis with the year's data
      return {
        ...data,
        current_data: {
          ...data.current_data,
          year: yearData.year,
          total_tax_single: yearData.total_tax_single,
          tax_burden_single: yearData.tax_burden_single,
          tax_freedom_day_single: yearData.tax_freedom_day_single,
          total_tax_married: yearData.total_tax_married,
          tax_burden_married: yearData.tax_burden_married,
          tax_freedom_day_married: yearData.tax_freedom_day_married,
          total_tax_family: yearData.total_tax_family,
          tax_burden_family: yearData.tax_burden_family,
          tax_freedom_day_family: yearData.tax_freedom_day_family,
        },
      }
    },
    [data]
  )

  const displayData = getDataForYear(selectedYear)

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={displayData} t={t} />
      case 'trends':
        return <TrendsTab data={data} t={t} />
      default:
        return <OverviewTab data={displayData} t={t} />
    }
  }, [activeTab, displayData, data, t])

  return (
    <TaxContent
      data={data}
      loading={loading}
      error={error}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      selectedYear={selectedYear}
      onYearChange={setSelectedYear}
      availableYears={availableYears}
      renderTabContent={renderTabContent}
      locationName={entity?.npa ? `${entity.npa}` : entity?.municipality_name}
      cantonName={entity?.canton_name}
      readOnly={readOnly}
    />
  )
}
